import { Directive, input, output } from '@angular/core';
import { outputFromObservable, outputToObservable, takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilArrayChanged, distinctUntilDeepChanged } from '@examdojo/core/rxjs';
import { GridApi, GridOptions, GridReadyEvent, SelectionChangedEvent } from 'ag-grid-community';
import { BehaviorSubject, combineLatest, debounceTime, filter, map, tap } from 'rxjs';
import { TreeNode, TreeNodeMetadata } from './tree-node.model';

@Directive()
export abstract class SelectableTreeBaseComponent<
  OriginalData = unknown,
  Metadata extends TreeNodeMetadata | void = void,
> {
  protected constructor(
    gridOptions: Partial<GridOptions<TreeNode<OriginalData, Metadata>>> &
      Required<Pick<GridOptions<TreeNode<OriginalData, Metadata>>, 'autoGroupColumnDef'>>,
  ) {
    this.gridOptions = {
      treeData: true,
      groupDefaultExpanded: -1,
      animateRows: true,
      rowHeight: 32,
      suppressGroupRowsSticky: true,
      rowClass: 'node-row',
      rowSelection: 'multiple',
      rowMultiSelectWithClick: true,
      groupSelectsChildren: true,
      columnDefs: [],
      // groupSuppressAutoColumn: true, // Not available in latest AG Grid?
      // For treeData, we might need to just rely on columnDefs being set.
      // If autoGroupColumnDef is NOT set, but we have columnDefs, it should use our column.
      // However, treeData usually mandates a group column.
      getDataPath: (data) => {
        return data.path;
      },
      getRowId: (params) => params.data.id,
      defaultColDef: {
        flex: 1,
      },
      onRowClicked: (event) => {
        if (event.node.group) {
          event.node.setExpanded(!event.node.expanded);
          return;
        }
      },
      ...gridOptions,
    };

    this.syncNodesAndSelectionEffect().pipe(takeUntilDestroyed()).subscribe();
  }

  readonly explorerNodes = input([] as Array<TreeNode<OriginalData, Metadata>>, {
    transform: (values: Array<TreeNode<OriginalData, Metadata>> | undefined | null) => values ?? [],
  });

  readonly selectedIds = input([] as string[], {
    transform: (values: string[] | undefined | null) => values ?? [],
  });

  readonly selectedNodesChange = output<Array<TreeNode<OriginalData, Metadata>>>();
  readonly selectedIdsChange = outputFromObservable(
    outputToObservable(this.selectedNodesChange).pipe(map((nodes) => nodes.map((node) => node.id))),
  );

  protected gridApi?: GridApi<TreeNode<OriginalData, Metadata>>;
  private readonly isGridApiReady$$ = new BehaviorSubject(false);

  readonly gridOptions: GridOptions<TreeNode<OriginalData, Metadata>>;

  onGridReady(params: GridReadyEvent<TreeNode<OriginalData, Metadata>>) {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
    this.isGridApiReady$$.next(true);
  }

  onSelectionChanged(event: SelectionChangedEvent): void {
    // Ignore events triggered by our own sync operation
    if (event.source === 'rowDataChanged') {
      return;
    }

    const selectedNodes = this.gridApi?.getSelectedNodes() ?? [];
    const nodes = selectedNodes.map((node) => node.data!);
    this.selectedNodesChange.emit(nodes);
  }

  private syncNodesAndSelectionEffect() {
    return combineLatest([
      this.isGridApiReady$$.pipe(filter(Boolean)),
      toObservable(this.explorerNodes).pipe(distinctUntilDeepChanged()),
      toObservable(this.selectedIds).pipe(distinctUntilArrayChanged()),
    ]).pipe(
      debounceTime(0),
      tap(([isGridApiReady, nodes, selectedIds]) => {
        if (!isGridApiReady || !this.gridApi) {
          return;
        }

        this.gridApi.setGridOption('rowData', nodes);
        this.gridApi.refreshCells({ force: true });

        this.gridApi.forEachNode((node) => {
          if (!node.data) {
            return;
          }

          const isGroupAndSelectsChildren = node.group && this.gridApi?.getGridOption('groupSelectsChildren');
          if (isGroupAndSelectsChildren) {
            return;
          }

          const shouldBeSelected = selectedIds.includes(node.data.id);
          if (node.isSelected() === shouldBeSelected) {
            return;
          }

          node.setSelected(shouldBeSelected, false, 'rowDataChanged');
        });
      }),
    );
  }
}
